// PAY2-S2 (docs/build-briefs/PAY2-S2-monthly-royalty-statement.md; DEC-404, DEC-405): the pure decisions behind the
// monthly Royalty statement, shared by the franchisor console and the location's own Reports. No DOM, no fetch. The server
// does every calculation; this module only reads its answer, labels months and formats cents.

import { formatCents } from "./money.ts";
import type { GateExportFormat } from "./exportMenuModel.ts";

export const STATEMENT_FRANCHISOR_PATH = "/franchisor/reports/royalty-statement";
export const STATEMENT_FAILED = "The statement could not be loaded. Try again.";
export const STATEMENT_DOWNLOAD_FAILED = "The file could not be downloaded. Try again.";
export const STATEMENT_NA = "-";
export const STATEMENT_COLUMNS = [
  "Franchisee",
  "Location",
  "Net sales",
  "Rate",
  "Royalty due",
  "Status",
  "Collection",
] as const;

export interface StatementSegmentDto {
  from: string;
  to: string;
  days: number;
  rateText: string;
  netCents: number;
  royaltyCents: number;
}

/** PAY2-S7. How the month's royalty is being collected: the server's plain label and tone, and the amount. */
export interface StatementCollectionDto {
  status: string;
  label: string;
  tone: "good" | "warn" | "bad" | "neutral";
  amountCents: number | null;
}

export interface StatementRowDto {
  franchisee: string;
  locationName: string;
  netCents: number;
  rateText: string;
  royaltyDueCents: number;
  /** FRADFUND1: the fund rate in force ("2.00%" or "Not set") and the fund fee due, separate from royalty. */
  fundRateText?: string;
  fundDueCents?: number;
  openDays: number;
  /** FRANROY1: the month is not over, so the figures are to date. */
  inProgress?: boolean;
  /** FRANROY1: days up to today (or month end) that are not closed. */
  unclosedDays?: number;
  /** FRANROY1: "$1,200.00 a month, $1,084.00 so far" for a flat royalty, else null. */
  flatFeeText?: string | null;
  status: "final" | "open";
  statusText: string;
  segments: StatementSegmentDto[];
  /** Absent or null: no charge for the month. */
  collection?: StatementCollectionDto | null;
}

export interface RoyaltyStatementDto {
  month: string;
  from: string;
  to: string;
  /** FRANROY1: the month is not over; every figure is to `asOf`. */
  inProgress?: boolean;
  asOf?: string | null;
  rows: StatementRowDto[];
  totals: { netCents: number; royaltyDueCents: number; fundDueCents?: number };
}

function isRow(value: unknown): boolean {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v["franchisee"] === "string" &&
    typeof v["locationName"] === "string" &&
    typeof v["netCents"] === "number" &&
    typeof v["rateText"] === "string" &&
    typeof v["royaltyDueCents"] === "number" &&
    typeof v["statusText"] === "string" &&
    (v["status"] === "final" || v["status"] === "open") &&
    Array.isArray(v["segments"])
  );
}

export function isStatementShape(value: unknown): value is RoyaltyStatementDto {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  const totals = v["totals"] as Record<string, unknown> | null | undefined;
  return (
    typeof v["month"] === "string" &&
    /^\d{4}-\d{2}$/.test(v["month"]) &&
    Array.isArray(v["rows"]) &&
    (v["rows"] as unknown[]).every(isRow) &&
    typeof totals === "object" &&
    totals !== null &&
    typeof totals["netCents"] === "number" &&
    typeof totals["royaltyDueCents"] === "number"
  );
}

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"] as const;

/** "Feb 2026" from "2026-02". An unreadable value reads N/A, never a made-up month. */
export function monthLabel(month: string): string {
  const match = /^(\d{4})-(\d{2})$/.exec(month);
  const name = match === null ? undefined : MONTH_NAMES[Number(match[2]) - 1];
  return match === null || name === undefined ? STATEMENT_NA : `${name} ${match[1]}`;
}

function shiftMonth(month: string, by: number): string {
  const year = Number(month.slice(0, 4));
  const index = Number(month.slice(5, 7)) - 1 + by;
  const y = year + Math.floor(index / 12);
  const m = ((index % 12) + 12) % 12;
  return `${String(y).padStart(4, "0")}-${String(m + 1).padStart(2, "0")}`;
}

export interface MonthOption {
  value: string;
  label: string;
}

/**
 * The months the picker offers: the month after the server's default (the current month, which reads as open) and the
 * eleven before the default. Built from the server's own default month, so the browser clock decides nothing.
 */
export function monthOptions(defaultMonth: string): MonthOption[] {
  const out: MonthOption[] = [];
  for (let by = 1; by >= -11; by -= 1) {
    const value = shiftMonth(defaultMonth, by);
    out.push({ value, label: monthLabel(value) });
  }
  return out;
}

export function moneyCell(cents: number): string {
  return formatCents(cents);
}

export function queryFor(month: string | null, format?: GateExportFormat): string {
  const parts: string[] = [];
  if (month !== null) parts.push(`month=${encodeURIComponent(month)}`);
  if (format !== undefined) parts.push(`format=${format}`);
  return parts.length === 0 ? "" : `?${parts.join("&")}`;
}

const COLLECTION_TONES: readonly string[] = ["good", "warn", "bad", "neutral"];

/** The Collection cell's text and chip tone. A month with no charge reads N/A with no chip. */
export function collectionCell(
  row: Pick<StatementRowDto, "collection">,
): { text: string; tone: "good" | "warn" | "bad" | "neutral" | null } {
  const c = row.collection;
  if (c === undefined || c === null || typeof c.label !== "string" || !COLLECTION_TONES.includes(c.tone)) {
    return { text: STATEMENT_NA, tone: null };
  }
  return { text: c.label, tone: c.tone };
}

/** A status chip tone: Final is good, anything open is a warning. */
export function statusTone(row: Pick<StatementRowDto, "status">): "good" | "warn" {
  return row.status === "final" ? "good" : "warn";
}

/** FRANROY1: the chip over a monthly statement. A month in progress says so; a closed month says Final or what is still open. */
export function monthStateText(statement: Pick<RoyaltyStatementDto, "inProgress" | "asOf" | "rows">): { text: string; tone: "info" | "good" | "warn" } {
  if (statement.inProgress === true) {
    const through = statement.asOf === null || statement.asOf === undefined ? "" : ` through ${shortDate(statement.asOf)}`;
    return { text: `Month in progress, not final${through}`, tone: "info" };
  }
  const open = statement.rows.filter((row) => row.status === "open").length;
  if (open === 0) return { text: "Final", tone: "good" };
  return { text: `Not final, ${open} ${open === 1 ? "location has" : "locations have"} days not closed`, tone: "warn" };
}

function shortDate(iso: string): string {
  const match = /^\d{4}-(\d{2})-(\d{2})/.exec(iso);
  const name = match === null ? undefined : MONTH_NAMES[Number(match[1]) - 1];
  return match === null || name === undefined ? iso : `${name} ${Number(match[2])}`;
}
